import { useMemo } from 'react';

import { useCodeFlavor, type CodeFlavor } from '../../lib/code-flavor-context';
import { useNetwork } from '../../lib/network-context';
import { TerminalCodeBlock, type CodeTab } from './CodeBlock';

const TS_TABS = (k: string, w: string): CodeTab[] => [
  { id: 'kit', label: '@solana/kit', source: k, lang: 'typescript' },
  { id: 'web3js', label: '@solana/web3.js', source: w, lang: 'typescript' },
];

export function EventsListenerPage() {
  const { rpcUrl } = useNetwork();
  const { flavor, setFlavor } = useCodeFlavor();
  const onTab = (id: string) => setFlavor(id as CodeFlavor);

  const wsUrl = rpcUrl.replace(/^http/i, 'ws');

  const enableTabs = useMemo(
    () =>
      TS_TABS(
        `import { setSdkConfig } from '@sly-rentals/core';

// Add wsUrl + eventsListener to the same setSdkConfig call you make at startup.
setSdkConfig({
  network: 'atlasnet',
  rpcUrl: '${rpcUrl}',
  wsUrl: '${wsUrl}',           // logsSubscribe endpoint — most providers
                                // host this at the same host under wss://
  eventsListener: true,         // explicit on/off; without this the
                                // singleton listener stays dormant
});`,
        `// Live events use the kit subpath under the hood; wsUrl + eventsListener
// belong on the kit-side setSdkConfig regardless of which entry point your
// app builds instructions through.
import { setSdkConfig } from '@sly-rentals/core';

setSdkConfig({
  network: 'atlasnet',
  rpcUrl: '${rpcUrl}',
  wsUrl: '${wsUrl}',
  eventsListener: true,
});`
      ),
    [rpcUrl, wsUrl]
  );

  const liveTabs = useMemo(
    () =>
      TS_TABS(
        `import { events, closeSrslyEventListener } from '@sly-rentals/core';

// Single event — \`evt\` is narrowed to RentalAcceptedEvent by autocomplete.
const off = events.rental.accepted.on(evt => {
  console.log('new rental', evt.rentalState, 'escrow', evt.escrow);
});

// Group-scoped — fires for every rental.* event; \`evt.type\` is the union
// of just rental events.
events.rental.all.on(evt => console.log(evt.type, evt.signature));

// Global catch-all
events.all.on(evt => console.log(evt.type));

// Tear down individual handlers, or the whole listener:
off();
await closeSrslyEventListener();`,
        `import { events, closeSrslyEventListener } from '@sly-rentals/core/legacy';

const off = events.rental.accepted.on(evt => {
  console.log('new rental', evt.rentalState, 'escrow', evt.escrow);
});

events.rental.all.on(evt => console.log(evt.type));
events.all.on(evt => console.log(evt.type));

off();
await closeSrslyEventListener();`
      ),
    []
  );

  const txTabs = useMemo(
    () =>
      TS_TABS(
        `import { acceptRental, getEventsFromTx } from '@sly-rentals/core';

// After you've submitted an instruction and have a signature back, pull
// the resulting events out of the tx without a live subscription. Same
// handler registry — handlers registered through \`events.*\` fire here too.
const ixs = await acceptRental(params);
const sig = await wallet.sendAndConfirm(ixs);

const txEvents = await getEventsFromTx(sig);
const accepted = txEvents.find(e => e.type === 'RentalAccepted');
console.log(accepted?.escrow, accepted?.borrower);`,
        `import { acceptRental, getEventsFromTx } from '@sly-rentals/core/legacy';

const instructions = await acceptRental(params);
const sig = await wallet.sendAndConfirm(instructions);

const txEvents = await getEventsFromTx(sig);
const accepted = txEvents.find(e => e.type === 'RentalAccepted');
console.log(accepted?.escrow, accepted?.borrower);`
      ),
    []
  );

  return (
    <div>
      <div className="kicker">[ 00 / events listener ]</div>
      <h1 className="page-title">
        Decode SRSLY events <em>without Anchor.</em>
      </h1>
      <p className="page-sub">
        The SDK ships a borsh decoder for the program's six emitted events plus a singleton listener
        that wraps Solana's <span className="mono cyan">logsSubscribe</span>. Subscribe once, get
        fully-typed event payloads — no <span className="mono cyan">@coral-xyz/anchor</span>{' '}
        dependency, no IDL to bundle. The toast feed at the bottom-right of this page is the same
        listener; the <span className="mono cyan">EVENTS</span> pill in the network bar toggles it.
      </p>

      <div className="section-head">
        <h2>1 — Enable the listener</h2>
        <p>
          Add a WebSocket URL and the <span className="mono cyan">eventsListener: true</span> flag
          to your existing <span className="mono cyan">setSdkConfig</span> call. The flag is
          deliberate so a stray <span className="mono cyan">wsUrl</span> set for some other reason
          doesn't accidentally open a subscription.
        </p>
      </div>
      <TerminalCodeBlock tabs={enableTabs} active={flavor} onActiveChange={onTab} meta="ts" />

      <div className="section-head">
        <h2>2 — Subscribe to live events</h2>
        <p>
          The <span className="mono cyan">events</span> tree is a typed namespace derived from the
          program's event manifest. Drill in by group or by individual event —{' '}
          <span className="mono cyan">events.rental.accepted</span>,{' '}
          <span className="mono cyan">events.rental.all</span>,{' '}
          <span className="mono cyan">events.owner.paid</span>,{' '}
          <span className="mono cyan">events.all</span> — and the handler arg narrows to the
          matching event shape automatically. Adding a new event upstream lights up new autocomplete
          paths with no consumer-side edits.
        </p>
      </div>
      <TerminalCodeBlock tabs={liveTabs} active={flavor} onActiveChange={onTab} meta="ts" />

      <div className="section-head">
        <h2>3 — Or pull events from a single tx</h2>
        <p>
          Useful right after submitting an instruction — you already have the signature, you just
          want the typed event payload back. Works without a WebSocket; falls back to{' '}
          <span className="mono cyan">getTransaction</span> on the configured{' '}
          <span className="mono cyan">rpcUrl</span>. Dispatches into the same handler registry, so a
          handler registered once fires whether the event arrived live or via this lookup.
        </p>
      </div>
      <TerminalCodeBlock tabs={txTabs} active={flavor} onActiveChange={onTab} meta="ts" />

      <div className="section-head">
        <h2>Notes</h2>
        <p>
          Heads-up: <span className="mono cyan">api.mainnet-beta.solana.com</span> and other free
          public RPCs typically rate-limit or disable{' '}
          <span className="mono cyan">logsSubscribe</span>. Use a custom RPC (Helius, Triton,
          QuickNode, or your own validator) if the listener stays quiet on a network where you
          expect activity. The <span className="mono cyan">EVENTS</span> pill turns mint-green when
          the singleton is live; flip it off to drop the WebSocket connection without unmounting
          your handlers.
        </p>
      </div>
    </div>
  );
}
